import { useMemo } from 'react';
import type { VersionEntry, VersionChange, WhatsNewResponse } from '../../types';

const categoryConfig: Record<VersionChange['category'], { label: string; bg: string; text: string }> = {
  feature: { label: 'Feature', bg: 'bg-blue-500/20', text: 'text-blue-400' },
  fix: { label: 'Bug Fix', bg: 'bg-red-500/20', text: 'text-red-400' },
  improvement: { label: 'Improvement', bg: 'bg-green-500/20', text: 'text-green-400' },
  breaking: { label: 'Breaking', bg: 'bg-amber-500/20', text: 'text-amber-400' },
  security: { label: 'Security', bg: 'bg-purple-500/20', text: 'text-purple-400' },
  documentation: { label: 'Documentation', bg: 'bg-slate-600/40', text: 'text-slate-300' },
  test: { label: 'Tests', bg: 'bg-teal-500/20', text: 'text-teal-400' },
  removed: { label: 'Removed', bg: 'bg-orange-500/20', text: 'text-orange-400' },
  note: { label: 'Note', bg: 'bg-slate-700/60', text: 'text-slate-300' },
};

function formatReleaseDate(releasedAt: string): string {
  try {
    return new Date(releasedAt).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return releasedAt;
  }
}

function groupChangesByCategory(changes: VersionChange[]): Record<VersionChange['category'], VersionChange[]> {
  const grouped: Record<VersionChange['category'], VersionChange[]> = {
    feature: [],
    fix: [],
    improvement: [],
    breaking: [],
    security: [],
    documentation: [],
    test: [],
    removed: [],
    note: [],
  };

  for (const change of changes) {
    const bucket = Object.hasOwn(grouped, change.category) ? grouped[change.category] : grouped.note;
    bucket.push(change);
  }

  return grouped;
}

interface WhatsNewModalProps {
  data: WhatsNewResponse;
  onDismiss: () => void;
}

function VersionSection({ version }: { version: VersionEntry }) {
  const groupedChanges = useMemo(() => groupChangesByCategory(version.changes), [version.changes]);

  return (
    <div className="bg-slate-800 border border-slate-700 rounded-lg p-4 space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <span className="inline-flex items-center rounded-md bg-slate-700 border border-slate-600 px-2.5 py-1 text-xs font-mono text-slate-200">
          v{version.version}
        </span>
        <span className="text-sm text-slate-400">{formatReleaseDate(version.releasedAt)}</span>
      </div>

      <p className="text-sm text-slate-200">{version.summary}</p>

      <div className="space-y-2">
        {(Object.keys(categoryConfig) as VersionChange['category'][]).map((category) => {
          const items = groupedChanges[category];
          if (!items || items.length === 0) return null;

          const cfg = categoryConfig[category];
          return (
            <section key={category} className="space-y-1">
              <span
                className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${cfg.bg} ${cfg.text}`}
              >
                {cfg.label}
              </span>
              <ul className="space-y-0.5">
                {items.map((change, index) => (
                  <li key={`${category}-${index}`} className="text-sm text-slate-300 flex items-start">
                    <span className="mr-2 mt-1 text-slate-500">•</span>
                    <span>{change.description}</span>
                  </li>
                ))}
              </ul>
            </section>
          );
        })}
      </div>
    </div>
  );
}

export function WhatsNewModal({ data, onDismiss }: WhatsNewModalProps) {
  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="whats-new-title"
    >
      <div className="bg-slate-900 border border-slate-700 rounded-xl shadow-2xl max-w-lg w-full mx-4 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="p-5 border-b border-slate-700 flex-shrink-0">
          <div className="flex items-center gap-3">
            <span className="text-2xl" role="img" aria-label="sparkles">✨</span>
            <div>
              <h2 id="whats-new-title" className="text-lg font-semibold text-slate-100">
                What&apos;s New
              </h2>
              <p className="text-sm text-slate-400">
                Version {data.currentVersion}
              </p>
            </div>
          </div>
        </div>

        {/* Scrollable content */}
        <div className="p-5 overflow-y-auto flex-1 space-y-4">
          {data.entries.map((entry) => (
            <VersionSection key={entry.version} version={entry} />
          ))}
        </div>

        {/* Footer */}
        <div className="p-5 border-t border-slate-700 flex-shrink-0">
          <button
            onClick={onDismiss}
            className="w-full px-4 py-2.5 text-sm font-medium text-white bg-primary-700 hover:bg-primary-800 rounded-lg transition-colors"
            aria-label="Dismiss what's new popup"
          >
            Got it!
          </button>
        </div>
      </div>
    </div>
  );
}
